import {
  defaultShouldDehydrateQuery,
  isServer,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import superjson from 'superjson';
import { toast } from 'sonner';

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 0 * 1_000,
        // Note: We replicate React Query's built-in default here:
        // Only throw an error if the query has *no* cached data (i.e. data === undefined).
        // This lets components continue to render with stale data while background refetches
        // can still surface errors via onError handlers or error boundaries when necessary.
        // Source: Suspense guide – "throwOnError default"
        // https://raw.githubusercontent.com/TanStack/query/main/docs/framework/react/guides/suspense.md#throwonerror-default
        throwOnError: (_, query) => typeof query.state.data === 'undefined',
      },
      dehydrate: {
        serializeData: superjson.serialize,
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === 'pending',
      },
      hydrate: {
        deserializeData: superjson.deserialize,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // 🎉 only show error toasts if we already have data in the cache
        // which indicates a failed background update
        if (query.state.data === undefined) {
          console.error(error);
          toast.error('Something went wrong');
        }
      },
    }),
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  }

  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  return (browserQueryClient ??= makeQueryClient());
}
